import { ChevronLeftIcon, ChevronRightIcon, Loader2Icon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { pdfjs } from 'react-pdf';
import { Rnd } from 'react-rnd';
import { toast } from 'sonner';

import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogBody,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import { Input } from '@/components/ui/input';
import { Z_INDEX_MODAL_DEEPLY_NESTED } from '@/components/ui/zIndex';
import { useHipaaAuthorizationCreateOrUpdateMutation } from '@/services/api/hipaaAuthorizationService';
import type { Blob as MatterBlob } from '@/services/types/blob-types';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface MatterSignatureCaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
  blob: MatterBlob | null;
  clientId?: string;
  onUploadComplete?: () => void;
  /** When provided, overrides the default HIPAA authorization save flow. Receives the cropped file and signature date. */
  onSaveFile?: (file: File, signatureDate: string) => Promise<void>;
}

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface DisplayedImageDimensions {
  width: number;
  height: number;
  naturalWidth: number;
  naturalHeight: number;
}

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    if (!url.startsWith('data:') && !url.startsWith('blob:')) {
      image.crossOrigin = 'anonymous';
    }
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Unable to load image'));
    image.src = url;
  });

const getScaleFactor = (natural: number, displayed: number) =>
  displayed > 0 ? natural / displayed : 1;

const isPdfBlob = (blob: MatterBlob | null) => {
  if (!blob) return false;

  const filename = (blob.original_filename || blob.name || '').toLowerCase();
  return blob.file_mime_type === 'application/pdf' || filename.endsWith('.pdf');
};

const getBlobTypeLabel = (blob: MatterBlob | null) => {
  if (!blob) return 'File';
  return isPdfBlob(blob) ? 'PDF' : 'Image';
};

const renderPdfPageAsImage = async ({
  fileUrl,
  pageNumber,
}: {
  fileUrl: string;
  pageNumber: number;
}) => {
  const loadingTask = pdfjs.getDocument(fileUrl);
  const pdfDocument = await loadingTask.promise;
  const page = await pdfDocument.getPage(pageNumber);
  const viewport = page.getViewport({ scale: 2 });
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Unable to initialize PDF rendering');
  }

  canvas.width = viewport.width;
  canvas.height = viewport.height;

  await page.render({
    canvasContext: context,
    viewport,
  }).promise;

  return {
    dataUrl: canvas.toDataURL('image/png'),
    numPages: pdfDocument.numPages,
  };
};

const createCroppedFile = async ({
  sourceUrl,
  croppedAreaPixels,
  originalFileName,
}: {
  sourceUrl: string;
  croppedAreaPixels: CropArea;
  originalFileName: string;
}): Promise<File> => {
  const image = await createImage(sourceUrl);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(croppedAreaPixels.width));
  canvas.height = Math.max(1, Math.round(croppedAreaPixels.height));
  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Unable to initialize image processing');
  }

  context.drawImage(
    image,
    croppedAreaPixels.x,
    croppedAreaPixels.y,
    croppedAreaPixels.width,
    croppedAreaPixels.height,
    0,
    0,
    canvas.width,
    canvas.height,
  );

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob((value) => resolve(value), 'image/png');
  });

  if (!blob) {
    throw new Error('Failed to generate cropped signature image');
  }

  const fileRoot = originalFileName.replace(/\.[^/.]+$/, '') || 'signature';
  return new File([blob], `${fileRoot}-signature.png`, { type: 'image/png' });
};

export const MatterSignatureCaptureModal = ({
  isOpen,
  onClose,
  blob,
  clientId,
  onUploadComplete,
  onSaveFile,
}: MatterSignatureCaptureModalProps) => {
  const imageRef = useRef<HTMLImageElement | null>(null);
  const imageFrameRef = useRef<HTMLButtonElement | null>(null);
  const cropSourceObjectUrlRef = useRef<string | null>(null);
  const [signatureDate, setSignatureDate] = useState(
    new Date().toISOString().split('T')[0],
  );
  const [pageNumber, setPageNumber] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [cropSourceUrl, setCropSourceUrl] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isLoadingSource, setIsLoadingSource] = useState(false);
  const [sourceError, setSourceError] = useState<string | null>(null);
  const [selectionRect, setSelectionRect] = useState<CropArea | null>(null);
  const [drawStartPoint, setDrawStartPoint] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [displayedImageDimensions, setDisplayedImageDimensions] =
    useState<DisplayedImageDimensions | null>(null);

  const [uploadSignature, { isLoading: isUploading }] =
    useHipaaAuthorizationCreateOrUpdateMutation();

  const isPdf = useMemo(() => isPdfBlob(blob), [blob]);
  const sourceName = blob?.name || blob?.original_filename || 'Selected file';

  useEffect(() => {
    if (!isOpen || !blob) {
      if (cropSourceObjectUrlRef.current) {
        URL.revokeObjectURL(cropSourceObjectUrlRef.current);
        cropSourceObjectUrlRef.current = null;
      }
      setCropSourceUrl(null);
      setPreviewUrl(null);
      setSourceError(null);
      return;
    }

    setSignatureDate(new Date().toISOString().split('T')[0]);
    setPageNumber(1);
    setPageCount(1);
    setSelectionRect(null);
    setDrawStartPoint(null);
    setDisplayedImageDimensions(null);
    setPreviewUrl(null);
  }, [blob, isOpen]);

  useEffect(() => {
    if (!isOpen || !blob) {
      return undefined;
    }

    let isActive = true;

    const loadSource = async () => {
      setIsLoadingSource(true);
      setSourceError(null);

      try {
        if (isPdf) {
          const rendered = await renderPdfPageAsImage({
            fileUrl: blob.download_url,
            pageNumber,
          });

          if (!isActive) return;
          if (cropSourceObjectUrlRef.current) {
            URL.revokeObjectURL(cropSourceObjectUrlRef.current);
            cropSourceObjectUrlRef.current = null;
          }
          setCropSourceUrl(rendered.dataUrl);
          setPageCount(rendered.numPages);
        } else {
          const response = await fetch(blob.download_url, {
            credentials: 'include',
          });

          if (!response.ok) {
            throw new Error(`Unable to load image (${response.status})`);
          }

          const fileBlob = await response.blob();
          const objectUrl = URL.createObjectURL(fileBlob);

          if (!isActive) {
            URL.revokeObjectURL(objectUrl);
            return;
          }

          if (cropSourceObjectUrlRef.current) {
            URL.revokeObjectURL(cropSourceObjectUrlRef.current);
          }

          cropSourceObjectUrlRef.current = objectUrl;
          setCropSourceUrl(objectUrl);
          setPageCount(1);
        }
      } catch (error) {
        if (!isActive) return;

        setSourceError(
          error instanceof Error
            ? error.message
            : 'Unable to load the selected file for cropping.',
        );
        setCropSourceUrl(null);
      } finally {
        if (isActive) {
          setIsLoadingSource(false);
        }
      }
    };

    loadSource();

    return () => {
      isActive = false;
    };
  }, [blob, isOpen, isPdf, pageNumber]);

  useEffect(
    () => () => {
      if (cropSourceObjectUrlRef.current) {
        URL.revokeObjectURL(cropSourceObjectUrlRef.current);
        cropSourceObjectUrlRef.current = null;
      }
    },
    [],
  );

  useEffect(() => {
    if (!cropSourceUrl || !selectionRect || !displayedImageDimensions) {
      setPreviewUrl(null);
      return undefined;
    }

    let cancelled = false;

    const generatePreview = async () => {
      try {
        const image = await createImage(cropSourceUrl);
        const canvas = document.createElement('canvas');
        const scaleX = getScaleFactor(
          displayedImageDimensions.naturalWidth,
          displayedImageDimensions.width,
        );
        const scaleY = getScaleFactor(
          displayedImageDimensions.naturalHeight,
          displayedImageDimensions.height,
        );
        const croppedAreaPixels = {
          x: Math.round(selectionRect.x * scaleX),
          y: Math.round(selectionRect.y * scaleY),
          width: Math.round(selectionRect.width * scaleX),
          height: Math.round(selectionRect.height * scaleY),
        };
        const scale = Math.min(
          1,
          320 / Math.max(1, Math.round(croppedAreaPixels.width)),
        );
        canvas.width = Math.max(
          1,
          Math.round(croppedAreaPixels.width * scale),
        );
        canvas.height = Math.max(
          1,
          Math.round(croppedAreaPixels.height * scale),
        );
        const context = canvas.getContext('2d');
        if (!context) return;

        context.drawImage(
          image,
          croppedAreaPixels.x,
          croppedAreaPixels.y,
          croppedAreaPixels.width,
          croppedAreaPixels.height,
          0,
          0,
          canvas.width,
          canvas.height,
        );

        const nextPreviewUrl = canvas.toDataURL('image/png');
        if (!cancelled) {
          setPreviewUrl(nextPreviewUrl);
        }
      } catch {
        if (!cancelled) {
          setPreviewUrl(null);
        }
      }
    };

    const timeoutId = window.setTimeout(generatePreview, 80);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [cropSourceUrl, displayedImageDimensions, selectionRect]);

  const croppedAreaPixels = useMemo(() => {
    if (!selectionRect || !displayedImageDimensions) {
      return null;
    }

    const scaleX = getScaleFactor(
      displayedImageDimensions.naturalWidth,
      displayedImageDimensions.width,
    );
    const scaleY = getScaleFactor(
      displayedImageDimensions.naturalHeight,
      displayedImageDimensions.height,
    );

    return {
      x: Math.round(selectionRect.x * scaleX),
      y: Math.round(selectionRect.y * scaleY),
      width: Math.max(1, Math.round(selectionRect.width * scaleX)),
      height: Math.max(1, Math.round(selectionRect.height * scaleY)),
    };
  }, [displayedImageDimensions, selectionRect]);

  const syncDisplayedImageDimensions = useCallback(() => {
    const imageElement = imageRef.current;
    if (!imageElement) return;

    setDisplayedImageDimensions({
      width: imageElement.clientWidth,
      height: imageElement.clientHeight,
      naturalWidth: imageElement.naturalWidth,
      naturalHeight: imageElement.naturalHeight,
    });
  }, []);

  useEffect(() => {
    if (!cropSourceUrl) return undefined;

    const imageElement = imageRef.current;
    if (!imageElement) return undefined;

    syncDisplayedImageDimensions();

    const resizeObserver = new ResizeObserver(() => {
      syncDisplayedImageDimensions();
    });

    resizeObserver.observe(imageElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [cropSourceUrl, syncDisplayedImageDimensions]);

  const buildSelectionFromPoints = useCallback(
    (
      start: { x: number; y: number },
      current: { x: number; y: number },
    ): CropArea => {
      const maxX = displayedImageDimensions?.width ?? 0;
      const maxY = displayedImageDimensions?.height ?? 0;
      const clampedCurrent = {
        x: Math.min(Math.max(current.x, 0), maxX),
        y: Math.min(Math.max(current.y, 0), maxY),
      };

      return {
        x: Math.min(start.x, clampedCurrent.x),
        y: Math.min(start.y, clampedCurrent.y),
        width: Math.abs(clampedCurrent.x - start.x),
        height: Math.abs(clampedCurrent.y - start.y),
      };
    },
    [displayedImageDimensions],
  );

  useEffect(() => {
    if (!drawStartPoint) {
      return undefined;
    }

    const handleMouseMove = (event: MouseEvent) => {
      const frame = imageFrameRef.current;
      if (!frame) return;

      const bounds = frame.getBoundingClientRect();
      setSelectionRect(
        buildSelectionFromPoints(drawStartPoint, {
          x: event.clientX - bounds.left,
          y: event.clientY - bounds.top,
        }),
      );
    };

    const handleMouseUp = () => {
      setDrawStartPoint(null);
      setSelectionRect((current) => {
        if (!current || current.width < 12 || current.height < 12) {
          return null;
        }

        return current;
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [buildSelectionFromPoints, drawStartPoint]);

  const handleImageMouseDown = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (!displayedImageDimensions) return;

      const bounds = event.currentTarget.getBoundingClientRect();
      const startPoint = {
        x: Math.min(
          Math.max(event.clientX - bounds.left, 0),
          displayedImageDimensions.width,
        ),
        y: Math.min(
          Math.max(event.clientY - bounds.top, 0),
          displayedImageDimensions.height,
        ),
      };

      setDrawStartPoint(startPoint);
      setSelectionRect({
        x: startPoint.x,
        y: startPoint.y,
        width: 0,
        height: 0,
      });
    },
    [displayedImageDimensions],
  );

  const handleSave = useCallback(async () => {
    if (!blob || !cropSourceUrl || !croppedAreaPixels) {
      toast.error('Adjust the crop area before saving');
      return;
    }

    try {
      const croppedFile = await createCroppedFile({
        sourceUrl: cropSourceUrl,
        croppedAreaPixels,
        originalFileName: sourceName,
      });

      if (onSaveFile) {
        await onSaveFile(croppedFile, signatureDate);
      } else if (clientId) {
        await uploadSignature({
          clientId,
          body: {
            file: croppedFile,
            signature_date: signatureDate,
          },
        }).unwrap();
      }

      toast.success('HIPAA signature saved from matter file');
      onUploadComplete?.();
      onClose();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to save the cropped HIPAA signature',
      );
    }
  }, [
    blob,
    clientId,
    cropSourceUrl,
    croppedAreaPixels,
    onClose,
    onSaveFile,
    onUploadComplete,
    signatureDate,
    sourceName,
    uploadSignature,
  ]);

  let cropContent: JSX.Element;
  if (isLoadingSource) {
    cropContent = (
      <div className="flex h-full items-center justify-center text-zinc-200">
        <Loader2Icon className="mr-2 h-5 w-5 animate-spin" />
        Loading preview...
      </div>
    );
  } else if (sourceError) {
    cropContent = (
      <div className="flex h-full items-center justify-center px-8 text-center text-sm text-red-200">
        {sourceError}
      </div>
    );
  } else if (cropSourceUrl) {
    cropContent = (
      <div className="flex h-full justify-center overflow-auto bg-zinc-100 p-5">
        <div className="min-h-full">
          <button
            type="button"
            ref={imageFrameRef}
            className="relative inline-block cursor-crosshair border-0 bg-transparent p-0 text-left shadow-[0_8px_30px_rgba(15,23,42,0.12)]"
            aria-label="Signature crop canvas"
            onMouseDown={handleImageMouseDown}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                setSelectionRect(null);
              }
            }}
          >
            <img
              ref={imageRef}
              src={cropSourceUrl}
              alt="Signature crop source"
              onLoad={syncDisplayedImageDimensions}
              className="block max-w-full select-none"
              draggable={false}
            />

            {selectionRect ? (
              <Rnd
                size={{
                  width: selectionRect.width,
                  height: selectionRect.height,
                }}
                position={{
                  x: selectionRect.x,
                  y: selectionRect.y,
                }}
                bounds="parent"
                minWidth={24}
                minHeight={24}
                onDragStop={(_event, data) => {
                  setSelectionRect((current) =>
                    current
                      ? {
                          ...current,
                          x: data.x,
                          y: data.y,
                        }
                      : current,
                  );
                }}
                onResizeStop={(_event, _direction, ref, _delta, position) => {
                  setSelectionRect({
                    x: position.x,
                    y: position.y,
                    width: Number.parseFloat(ref.style.width),
                    height: Number.parseFloat(ref.style.height),
                  });
                }}
                resizeHandleStyles={{
                  bottomRight: {
                    width: 10,
                    height: 10,
                    right: -5,
                    bottom: -5,
                    background: '#ffffff',
                    border: '2px solid #4f46e5',
                    borderRadius: 9999,
                  },
                  bottomLeft: {
                    width: 10,
                    height: 10,
                    left: -5,
                    bottom: -5,
                    background: '#ffffff',
                    border: '2px solid #4f46e5',
                    borderRadius: 9999,
                  },
                  topRight: {
                    width: 10,
                    height: 10,
                    right: -5,
                    top: -5,
                    background: '#ffffff',
                    border: '2px solid #4f46e5',
                    borderRadius: 9999,
                  },
                  topLeft: {
                    width: 10,
                    height: 10,
                    left: -5,
                    top: -5,
                    background: '#ffffff',
                    border: '2px solid #4f46e5',
                    borderRadius: 9999,
                  },
                }}
                className="border-2 border-indigo-600 bg-indigo-500/10 shadow-[0_0_0_9999px_rgba(17,24,39,0.45)]"
                dragHandleClassName="signature-selection-drag-handle"
                onMouseDown={(event: MouseEvent) => {
                  event.stopPropagation();
                }}
              >
                <div className="signature-selection-drag-handle h-full w-full cursor-move" />
              </Rnd>
            ) : null}
          </button>
        </div>
      </div>
    );
  } else {
    cropContent = (
      <div className="flex h-full items-center justify-center px-8 text-center text-sm text-zinc-300">
        Select a file to start clipping.
      </div>
    );
  }

  const fileTypeLabel = getBlobTypeLabel(blob);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      size="5xl"
      className="sm:max-w-[min(88vw,1500px)]"
      nested
      zIndex={Z_INDEX_MODAL_DEEPLY_NESTED}
    >
      <DialogBody className="flex h-[min(80vh,1200px)] max-h-[min(80vh,1200px)] flex-col overflow-hidden !p-0">
        <div className="flex items-center justify-between border-b border-zinc-200 px-6 py-4">
          <div>
            <DialogTitle className="font-sans text-lg font-semibold text-zinc-950">
              Clip HIPAA signature
            </DialogTitle>
            <div className="mt-1 flex items-center gap-2">
              <p className="text-sm text-zinc-500">{sourceName}</p>
              <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] font-semibold text-zinc-600">
                {fileTypeLabel}
              </span>
            </div>
          </div>

          {isPdf && pageCount > 1 ? (
            <div className="flex items-center gap-2">
              <Button
                outline
                onClick={() => setPageNumber((current) => Math.max(1, current - 1))}
                disabled={pageNumber <= 1 || isLoadingSource}
                className="h-9 w-9 min-w-0 rounded-[10px] px-0"
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </Button>
              <div className="rounded-[10px] border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm font-medium text-zinc-700">
                Page {pageNumber} of {pageCount}
              </div>
              <Button
                outline
                onClick={() =>
                  setPageNumber((current) => Math.min(pageCount, current + 1))
                }
                disabled={pageNumber >= pageCount || isLoadingSource}
                className="h-9 w-9 min-w-0 rounded-[10px] px-0"
              >
                <ChevronRightIcon className="h-4 w-4" />
              </Button>
            </div>
          ) : null}
        </div>

        <div className="flex min-h-0 flex-1 gap-6 bg-zinc-50 px-6 py-6">
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[20px] border border-zinc-200 bg-white">
            <div className="border-b border-zinc-200 px-5 py-4">
              <p className="text-sm font-semibold text-zinc-950">
                Draw a box around only the handwritten signature
              </p>
              <p className="mt-1 text-sm text-zinc-500">
                Click and drag directly on the page to create a crop box, then
                move or resize it before saving.
              </p>
            </div>

            <div className="relative min-h-0 flex-1 overflow-hidden bg-zinc-950">
              {cropContent}
            </div>

            <div className="flex items-center justify-between gap-4 border-t border-zinc-200 px-5 py-4">
              <p className="text-sm text-zinc-500">
                {selectionRect
                  ? 'Drag inside the box to reposition it, or resize from the corners.'
                  : 'Click and drag anywhere on the page to create a crop box.'}
              </p>
              <Button
                outline
                onClick={() => setSelectionRect(null)}
                disabled={!selectionRect}
                className="h-9 min-w-[112px] shrink-0 rounded-[10px] px-4 whitespace-nowrap"
              >
                Reset box
              </Button>
            </div>
          </div>

          <div className="flex w-[320px] shrink-0 flex-col gap-4">
            <div className="rounded-[20px] border border-zinc-200 bg-white p-5">
              <p className="text-sm font-semibold text-zinc-950">
                Cropped signature preview
              </p>
              <div className="mt-4 flex h-[180px] items-center justify-center rounded-[18px] border border-dashed border-zinc-200 bg-zinc-50 p-4">
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Cropped signature preview"
                    className="max-h-full max-w-full object-contain"
                  />
                ) : (
                  <p className="text-center text-sm text-zinc-400">
                    Adjust the crop to preview the saved signature.
                  </p>
                )}
              </div>
            </div>

            <div className="rounded-[20px] border border-zinc-200 bg-white p-5">
              <p className="text-sm font-semibold text-zinc-950">
                Signature date
              </p>
              <Input
                id="signature-date"
                aria-label="Signature date"
                type="date"
                value={signatureDate}
                max={new Date().toISOString().split('T')[0]}
                onChange={(event) => setSignatureDate(event.target.value)}
                className="mt-3"
              />
              <ul className="mt-4 space-y-2 text-sm text-zinc-500">
                <li>Only the cropped signature image will be uploaded.</li>
                <li>The source matter file remains unchanged.</li>
                <li>Use another file if the signature is blurry or clipped.</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-zinc-200 bg-white px-6 py-4">
          <p className="text-sm text-zinc-500">
            This stays inside the current Review &amp; Submit flow.
          </p>
          <div className="flex items-center gap-3">
            <Button outline onClick={onClose} className="h-10 rounded-[10px]">
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={handleSave}
              disabled={
                !cropSourceUrl || !croppedAreaPixels || !signatureDate || isUploading
              }
              className="h-10 rounded-[10px]"
            >
              {isUploading ? (
                <>
                  <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Use as HIPAA signature'
              )}
            </Button>
          </div>
        </div>
      </DialogBody>
    </Dialog>
  );
};
